import React, { useEffect, useRef } from 'react';

// Brutalist, dark hero with 3D parallax canvas. Adapted from the Halide topo
// hero design — the original is TSX + Tailwind + shadcn, this is vanilla JSX
// with scoped CSS since the project uses CRA + global.css.
export default function HalideHero() {
  const canvasRef = useRef(null);
  const layersRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e) => {
      const x = (window.innerWidth / 2 - e.pageX) / 25;
      const y = (window.innerHeight / 2 - e.pageY) / 25;
      canvas.style.transform = `rotateX(${55 + y / 2}deg) rotateZ(${-25 + x / 2}deg)`;
      layersRef.current.forEach((layer, index) => {
        if (!layer) return;
        const depth = (index + 1) * 15;
        const moveX = x * (index + 1) * 0.2;
        const moveY = y * (index + 1) * 0.2;
        layer.style.transform = `translateZ(${depth}px) translate(${moveX}px, ${moveY}px)`;
      });
    };

    canvas.style.opacity = '0';
    canvas.style.transform = 'rotateX(90deg) rotateZ(0deg) scale(0.8)';
    const timeout = setTimeout(() => {
      canvas.style.transition = 'all 2.5s cubic-bezier(0.16, 1, 0.3, 1)';
      canvas.style.opacity = '1';
      canvas.style.transform = 'rotateX(55deg) rotateZ(-25deg) scale(1)';
    }, 300);

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <section className="halide-hero">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syncopate:wght@400;700&display=swap');

        .halide-hero {
          --hh-bg: #0a0a0a;
          --hh-silver: #e0e0e0;
          --hh-accent: #29B4F1;
          --hh-grain-opacity: 0.15;
          position: relative;
          background-color: var(--hh-bg);
          color: var(--hh-silver);
          font-family: 'Syncopate', 'Inter', sans-serif;
          overflow: hidden;
          height: auto;
          min-height: calc(100vh - 100px);
          width: auto;
          margin: 18px;
          border-radius: 28px;
        }
        .halide-hero .halide-grain {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 5;
          opacity: var(--hh-grain-opacity);
        }
        .halide-hero .viewport {
          position: absolute;
          inset: 0;
          perspective: 2000px;
          display: flex; align-items: center; justify-content: flex-end;
          padding-right: clamp(1rem, 3vw, 3rem);
          overflow: hidden;
        }
        .halide-hero .canvas-3d {
          position: relative;
          width: 680px; height: 440px;
          max-width: 52vw;
          transform-style: preserve-3d;
          transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .halide-hero .layer {
          position: absolute;
          inset: 0;
          border: none;
          border-radius: 8px;
          background-size: cover;
          background-position: center;
          transition: transform 0.5s ease;
        }
        .halide-hero .layer-1 { background-image: url('/hero-bg.png'); background-size: contain; background-repeat: no-repeat; background-color: #0a0a0a; }
        .halide-hero .layer-2 { background-image: url('/hero-bg.png'); background-size: contain; background-repeat: no-repeat; opacity: 0.5; mix-blend-mode: screen; }
        .halide-hero .layer-3 { background-image: url('/hero-bg.png'); background-size: contain; background-repeat: no-repeat; opacity: 0.35; mix-blend-mode: overlay; }
        .halide-hero .contours {
          position: absolute;
          width: 200%; height: 200%;
          top: -50%; left: -50%;
          background-image: repeating-radial-gradient(circle at 50% 50%, transparent 0, transparent 40px, rgba(255,255,255,0.05) 41px, transparent 42px);
          transform: translateZ(120px);
          pointer-events: none;
        }
        .halide-hero .interface-grid {
          position: relative;
          padding: clamp(2rem, 4vw, 3.5rem) clamp(2rem, 5vw, 4.5rem) clamp(2rem, 4vw, 3.5rem);
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto 1fr auto;
          gap: clamp(1.5rem, 3vw, 2.5rem);
          z-index: 10;
          pointer-events: none;
          min-height: calc(100vh - 100px);
        }
        .halide-hero .hero-title {
          grid-column: 1 / -1;
          align-self: center;
          font-size: clamp(1.5rem, 3vw, 3rem);
          line-height: 1.15;
          letter-spacing: -0.01em;
          mix-blend-mode: difference;
          margin: 0;
          max-width: 44%;
          color: var(--hh-silver);
          font-weight: 700;
          text-transform: uppercase;
        }
        .halide-hero .hero-title .accent {
          color: var(--hh-accent);
          mix-blend-mode: normal;
        }
        .halide-hero .hero-cta-row {
          grid-column: 1 / -1;
          align-self: end;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 2rem;
          flex-wrap: wrap;
        }
        .halide-hero .cta-button {
          pointer-events: auto;
          color: var(--hh-bg);
          padding: 1rem 2rem;
          text-decoration: none;
          font-weight: 700;
          font-family: inherit;
          letter-spacing: 0.05em;
          clip-path: polygon(0 0, 100% 0, 100% 70%, 85% 100%, 0 100%);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
          cursor: pointer;
          position: relative;
          overflow: visible;
          transition: transform 0.3s;
        }
        .halide-hero .cta-button-reveal {
          background: var(--hh-silver);
          padding-left: 3rem;
          color: var(--hh-bg);
        }
        .halide-hero .cta-button-reveal:hover {
          color: #fff !important;
          transform: translateY(-5px);
        }
        .halide-hero .cta-button-reveal::before {
          content: '';
          position: absolute;
          inset: 0;
          top: 50%;
          left: 1.5rem;
          width: 12px;
          height: 12px;
          background: var(--hh-accent);
          border-radius: 50%;
          transform: translate(-50%, -50%) scale(1);
          z-index: -1;
          transition: transform 0.6s cubic-bezier(0.35, 0, 0, 1);
          pointer-events: none;
        }
        .halide-hero .cta-button-reveal:hover::before {
          transform: translate(-50%, -50%) scale(50);
        }
        .halide-hero .cta-button-reveal:hover {
          color: #fff !important;
          transform: translateY(-5px);
        }
        .halide-hero .cta-button:not(.cta-button-reveal):hover { transform: translateY(-5px); }
        .halide-hero .cta-button.secondary {
          background: transparent;
          color: var(--hh-silver);
          border: 1px solid var(--hh-silver);
          clip-path: none;
        }
        .halide-hero .cta-button.secondary:hover { background: var(--hh-silver); color: var(--hh-bg); }
        .halide-hero .scroll-hint {
          position: absolute;
          bottom: 2rem; left: 50%;
          width: 1px; height: 60px;
          background: linear-gradient(to bottom, var(--hh-silver), transparent);
          animation: halideFlow 2s infinite ease-in-out;
          z-index: 11;
        }
        .halide-hero .subcopy {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 0.75rem;
          max-width: 520px;
          line-height: 1.6;
          color: var(--hh-silver);
        }
        .halide-hero .subcopy p { margin: 0; }
        .halide-hero .corner-meta {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          color: var(--hh-accent);
          font-size: 0.7rem;
          text-align: right;
          letter-spacing: 0.05em;
        }
        .halide-hero .brand-tag {
          font-weight: 700;
          letter-spacing: 0.15em;
          font-size: 0.85rem;
        }
        @keyframes halideFlow {
          0%, 100% { transform: scaleY(0); transform-origin: top; }
          50% { transform: scaleY(1); transform-origin: top; }
          51% { transform: scaleY(1); transform-origin: bottom; }
        }
        @media (max-width: 1024px) {
          .halide-hero {
            min-height: calc(100vh - 90px);
            margin: 12px;
            border-radius: 20px;
            display: flex;
            flex-direction: column;
          }
          .halide-hero .interface-grid { display: contents; }
          .halide-hero .corner-meta { display: none; }
          .halide-hero .hero-title {
            order: 1;
            max-width: 100%;
            /* Bigger headline so it fills the width on tablets, capped so it
               doesn't overflow on very narrow phones. */
            font-size: clamp(1.8rem, 8vw, 3.4rem);
            line-height: 1.1;
            margin: 0;
            padding: 1.5rem 1.25rem 0;
          }
          .halide-hero .viewport {
            order: 2;
            position: relative;
            inset: auto;
            flex: 1;
            min-height: 280px;
            width: 100%;
            padding: 1rem 1.25rem;
            align-items: stretch;
            justify-content: stretch;
            opacity: 1;
          }
          .halide-hero .canvas-3d {
            position: relative;
            max-width: 100%;
            width: 100%;
            height: auto;
            min-height: 260px;
            flex: 1;
          }
          .halide-hero .hero-cta-row {
            order: 3;
            flex-direction: column;
            align-items: stretch;
            gap: 1.25rem;
            padding: 0 1.25rem 2rem;
          }
          .halide-hero .cta-button {
            padding: 0.85rem 1.5rem;
            font-size: 0.85rem;
            white-space: nowrap;
            justify-content: center;
            clip-path: none;
          }
          .halide-hero .cta-button-reveal { padding-left: 2.5rem; }
          .halide-hero .cta-button-reveal::before,
          .halide-hero .cta-button-reveal:hover::before,
          .halide-hero .cta-button-reveal:focus::before,
          .halide-hero .cta-button-reveal:active::before {
            transform: translate(-50%, -50%) scale(1) !important;
          }
          .halide-hero .cta-button-reveal:hover { transform: none; }
          .halide-hero .scroll-hint { display: none; }
        }
      `}</style>

      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <filter id="halideGrain">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </svg>

      <div className="halide-grain" style={{ filter: 'url(#halideGrain)' }} />

      <div className="viewport">
        <div className="canvas-3d" ref={canvasRef}>
          <div className="layer layer-1" ref={(el) => (layersRef.current[0] = el)} />
          <div className="layer layer-2" ref={(el) => (layersRef.current[1] = el)} />
          <div className="layer layer-3" ref={(el) => (layersRef.current[2] = el)} />
          <div className="contours" />
        </div>
      </div>

      <div className="interface-grid">
        <div />
        <div className="corner-meta">
          <div>EST · 2026</div>
          <div>INVITATIONS · TEMPLATES</div>
        </div>

        <h1 className="hero-title">
          Every Simcha<br />
          deserves an invitation<br />
          <span className="accent">as special as the moment.</span>
        </h1>

        <div className="hero-cta-row">
          <div className="subcopy">
            <p>[ EVERY MOMENT · EVERY SIMCHA ]</p>
            <p>Beautifully designed templates you can personalize in minutes.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a href="#chooseCategorysec" className="cta-button cta-button-reveal">PICK A SIMCHA</a>
          </div>
        </div>
      </div>

      <div className="scroll-hint" />
    </section>
  );
}
